import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Newspaper, ArrowRight, CalendarDays } from "lucide-react";
import { PageHeader, Section } from "../components/ui-kit";
import { StayConnected } from "../components/StayConnected";
import { FacebookFeed } from "../components/FacebookFeed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const excerpt = (text, n = 160) => (text.length > n ? text.slice(0, n).trimEnd() + "…" : text);

export default function NewsList() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/news`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="News"
        title="What we've been up to"
        intro="Updates, trips and special moments from life at Spire Inclusion."
        testid="news-header"
      />
      <Section tone="default">
        <FacebookFeed />
      </Section>
      <Section tone="default">
        {posts === null ? (
          <div className="flex items-center gap-2 text-stone-600 py-10">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading news…
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16" data-testid="news-empty">
            <Newspaper className="mx-auto h-12 w-12 text-stone-400" aria-hidden="true" />
            <p className="mt-3 text-lg text-stone-600">No news posts yet. Please check back soon.</p>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="news-list">
            {posts.map((p) => (
              <article
                key={p.id}
                className="flex flex-col overflow-hidden rounded-2xl bg-white border border-stone-200 hover:shadow-md transition-shadow"
                data-testid={`news-card-${p.id}`}
              >
                {p.image_url && (
                  <Link to={`/news/${p.id}`} className="block aspect-[16/10] overflow-hidden bg-stone-200">
                    <img
                      src={`${BACKEND}${p.image_url}`}
                      alt={`Photo for the news post: ${p.title}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </Link>
                )}
                <div className="flex flex-1 flex-col p-6">
                  <p className="flex items-center gap-2 text-sm text-stone-500">
                    <CalendarDays className="h-4 w-4" aria-hidden="true" />
                    {fmtDate(p.created_at)}
                  </p>
                  <h2 className="mt-2 font-heading text-xl font-semibold text-stone-900">{p.title}</h2>
                  <p className="mt-2 text-base text-stone-700 leading-relaxed flex-1">{excerpt(p.body)}</p>
                  <Link
                    to={`/news/${p.id}`}
                    className="mt-4 inline-flex items-center gap-2 font-semibold text-orange-700 hover:text-orange-800"
                    data-testid={`news-read-${p.id}`}
                  >
                    Read more
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </Section>
      <StayConnected tone="white" />
    </>
  );
}
